import React, {createContext, useContext, useImperativeHandle} from 'react';
import {atom} from 'jotai';
import {
	useAttributeFormActions,
	type UseAttributeFormActionsParams,
} from '../hooks';
import type {AttributeAction} from '../types';

export type AttributeFormActionsContextType = ReturnType<
	typeof useAttributeFormActions
>;

const dummyAtom = atom<AttributeAction>('overwrite');

export const AttributeFormActionsContext =
	createContext<AttributeFormActionsContextType>({
		getAction: () => ({} as any),
		getActions: () => ({}),
		handleSubmit: async () => {},
		setAction: () => {},
		setActions: () => {},
		toggleAction: () => {},
		toggleActions: () => {},
		getActionAtom: () => dummyAtom,
	});

export const useAttributeFormActionsProvider = () => {
	const context = useContext(AttributeFormActionsContext);
	if (context === undefined) {
		throw new Error(
			'`useAttributeFormActionsProvider` must be used within a `AttributeFormActionsProvider`',
		);
	}
	return context;
};

export type AttributeFormActionsProviderRef =
	| React.RefObject<AttributeFormActionsContextType>
	| ((node: AttributeFormActionsContextType) => void)
	| null;

export type AttributeFormActionsProviderChildrenProps =
	AttributeFormActionsContextType;

export type AttributeFormActionsProviderProps =
	UseAttributeFormActionsParams & {
		ref?: AttributeFormActionsProviderRef;
		children:
			| React.ReactNode
			| ((props: AttributeFormActionsProviderChildrenProps) => React.ReactNode);
	};

const AttributeFormActionsProviderInner = (
	{children, ...props}: AttributeFormActionsProviderProps,
	ref: AttributeFormActionsProviderRef,
) => {
	const attributeForm = useAttributeFormActions(props);

	useImperativeHandle(ref, () => attributeForm, [attributeForm]);

	return (
		<AttributeFormActionsContext.Provider value={attributeForm}>
			{typeof children === 'function' ? children(attributeForm) : children}
		</AttributeFormActionsContext.Provider>
	);
};

export const AttributeFormActionsProviderPrimitive = React.forwardRef<
	AttributeFormActionsContextType,
	AttributeFormActionsProviderProps
>(AttributeFormActionsProviderInner);

/**
 * AttributeFormActions context provider
 * @example
 * <AttributeFormActionsProvider ref={attributeFormRef}>
 * 	...
 * </AttributeFormActionsProvider>
 * @example
 * <AttributeFormActionsProvider ref={attributeFormRef}>
 * 	{(attributeForm) => (
 * 		...
 * 	)}
 * </AttributeFormActionsProvider>
 */
export const AttributeFormActionsProvider = React.forwardRef(
	(
		props: AttributeFormActionsProviderProps,
		ref: AttributeFormActionsProviderRef,
	) => {
		return <AttributeFormActionsProviderPrimitive ref={ref} {...props} />;
	},
);

export default AttributeFormActionsProvider;
